import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stringify } from 'query-string';
import React from 'react';
import { useHistory } from 'react-router';
import Form from '@dashboard/components/Formik/Form';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { apiUrl } from '@dashboard/services/env';
import request, { fetcher } from '@dashboard/services/request';
import { templateCategoriesKey, templatesKey } from '@dashboard/lib/queryKeys';

const TemplateAdd = () => {
  const { emitToast } = useToast();
  const { token } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data: categories } = useQuery({
    queryKey: templateCategoriesKey,
    queryFn: () =>
      fetcher(
        `/templates/categories?${stringify({
          sort: JSON.stringify({ order: 1 }),
          limit: 99,
        })}`,
        token,
      ),
    enabled: !!token,
  });

  return (
    <div className="max-w-xl mx-auto mt-16">
      <h1 className="text-2xl font-bold mb-4">Add Template</h1>
      <Form
        initialValues={{
          title: '',
          page: '',
          category: '',
          publicTitle: '',
          publicDescription: '',
          displayAsUpsell: false,
          order: '',
          keywords: '',
        }}
        fields={[
          {
            name: 'title',
            required: true,
            type: 'text',
            label: 'Template title',
          },
          {
            name: 'page',
            required: true,
            type: 'text',
            label: 'Checkout ID',
          },
          {
            name: 'category',
            required: true,
            type: 'select',
            label: 'Category',
            options: [
              {
                label: 'Select a category',
                value: '',
              },
              ...(categories?.map(category => ({
                label: category.title,
                value: category._id,
              })) || []),
            ],
          },
          {
            name: 'displayAsUpsell',
            required: false,
            type: 'checkbox',
            label: 'Display as upsell',
          },
          {
            name: 'publicTitle',
            required: false,
            type: 'text',
            label: 'Page title',
          },
          {
            name: 'publicDescription',
            required: false,
            type: 'textarea',
            label: 'Page description',
            minRows: 8,
            autosize: true,
          },
          {
            name: 'order',
            required: false,
            type: 'number',
            label: 'Order',
            placeholder: 'Automatically generated if left empty',
          },
          {
            name: 'keywords',
            required: false,
            type: 'text',
            label: 'Keywords',
            tooltip: 'Used to generate content with GPT-3.5',
          },
        ]}
        showState={false}
        onSubmit={async values => {
          const response = await request(`${apiUrl}/templates`, token, {
            method: 'POST',
            body: JSON.stringify(values),
          });

          return {
            data: response.data,
            error: response,
            onSuccess: () => {
              emitToast({
                title: 'Template added',
              });
              queryClient.invalidateQueries({ queryKey: templatesKey });
              history.push(`/admin/templates/${response.data._id}`);
            },
          };
        }}
        submitButton={{
          label: 'Create template',
        }}
      />
    </div>
  );
};

export default TemplateAdd;
